import { useState, useEffect } from "react";
import axios from "axios";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { FileXls, MagnifyingGlass, Check, SpinnerGap, Download } from "@phosphor-icons/react";
import { toast } from "sonner";
import { downloadBase64File } from "@/utils/downloadHelper";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ExportSelectorDialog({ open, onClose, type }) {
  const [items, setItems] = useState([]);
  const [selected, setSelected] = useState([]);
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(false);

  const isFicha = type === "ficha_tecnica";
  const title = isFicha ? "Exportar Fichas Tecnicas" : "Exportar Lista de Compras";

  useEffect(() => {
    if (!open) return;
    setSelected([]);
    setSearch("");
    setLoading(true);
    const load = async () => {
      try {
        const endpoint = isFicha ? "/technical-sheets" : "/ingredients";
        const { data } = await axios.get(`${API}${endpoint}`);
        setItems(data || []);
      } catch { toast.error("Erro ao carregar dados"); }
      setLoading(false);
    };
    load();
  }, [open, isFicha]);

  const filtered = items.filter((i) => {
    const name = (i.name || i.nome || "").toLowerCase();
    const cat = (i.category || "").toLowerCase();
    const q = search.toLowerCase();
    return name.includes(q) || cat.includes(q);
  });

  const toggle = (id) => {
    setSelected((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    );
  };

  const selectAll = () => {
    if (selected.length === filtered.length) {
      setSelected([]);
    } else {
      setSelected(filtered.map((i) => i.id));
    }
  };

  const handleExport = async () => {
    if (selected.length === 0) {
      toast.error("Selecione pelo menos um item");
      return;
    }
    setExporting(true);
    try {
      const { data } = await axios.post(`${API}/export/generate`, {
        type,
        item_ids: selected,
      });
      const mime = data.is_zip
        ? "application/zip"
        : "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet";
      downloadBase64File(data.file_base64, data.file_name, mime);
      const desc = data.is_zip
        ? `${data.file_count} fichas exportadas em ZIP!`
        : `${selected.length} ${isFicha ? "fichas" : "ingredientes"} exportados!`;
      toast.success(desc);
      onClose();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erro ao exportar");
    }
    setExporting(false);
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="sm:max-w-[550px] max-h-[85vh] overflow-hidden flex flex-col" data-testid="export-selector-dialog">
        <DialogHeader>
          <DialogTitle className="text-lg font-semibold flex items-center gap-2">
            <FileXls size={20} className="text-emerald-600" />
            {title}
          </DialogTitle>
        </DialogHeader>

        {/* Search */}
        <div className="relative">
          <MagnifyingGlass size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400" />
          <Input
            placeholder={isFicha ? "Buscar fichas..." : "Buscar ingredientes..."}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-9 h-9 text-sm"
            data-testid="export-search"
          />
        </div>

        {/* Select all */}
        <div className="flex items-center justify-between px-1">
          <button
            onClick={selectAll}
            className="text-xs text-blue-600 hover:text-blue-800 font-medium"
            data-testid="export-select-all"
          >
            {selected.length === filtered.length && filtered.length > 0 ? "Desmarcar tudo" : `Selecionar tudo (${filtered.length})`}
          </button>
          <span className="text-xs text-zinc-400">
            {selected.length} selecionado{selected.length !== 1 ? "s" : ""}
          </span>
        </div>

        {/* Items list */}
        <div className="overflow-y-auto flex-1 max-h-[45vh] border rounded-lg divide-y divide-zinc-100">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <SpinnerGap size={24} className="text-zinc-300 animate-spin" />
            </div>
          ) : filtered.length === 0 ? (
            <div className="text-center py-8 text-zinc-400 text-sm">
              {search ? "Nenhum resultado" : "Nenhum item cadastrado"}
            </div>
          ) : (
            filtered.map((item) => {
              const id = item.id;
              const name = item.name || item.nome || "";
              const isSelected = selected.includes(id);

              return (
                <button
                  key={id}
                  onClick={() => toggle(id)}
                  className={`w-full flex items-center gap-3 px-3 py-2.5 text-left transition-colors ${
                    isSelected ? "bg-emerald-50" : "hover:bg-zinc-50"
                  }`}
                  data-testid={`export-item-${id}`}
                >
                  <div
                    className={`w-5 h-5 rounded border-2 flex items-center justify-center flex-shrink-0 transition-colors ${
                      isSelected
                        ? "bg-emerald-600 border-emerald-600"
                        : "border-zinc-300"
                    }`}
                  >
                    {isSelected && <Check size={12} weight="bold" className="text-white" />}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-zinc-900 truncate">{name}</p>
                    <div className="flex items-center gap-2 mt-0.5">
                      {isFicha ? (
                        <>
                          {item.yield_quantity && (
                            <span className="text-[10px] text-zinc-400">
                              Rend: {item.yield_quantity} {item.yield_unit}
                            </span>
                          )}
                          {item.total_cost > 0 && (
                            <span className="text-[10px] text-emerald-600">
                              R$ {Number(item.total_cost).toFixed(2)}
                            </span>
                          )}
                          {item.ingredients && (
                            <span className="text-[10px] text-zinc-400">
                              {item.ingredients.length} ingr.
                            </span>
                          )}
                        </>
                      ) : (
                        <>
                          <span className="text-[10px] text-zinc-400">{item.unit}</span>
                          {item.purchase_price > 0 && (
                            <span className="text-[10px] text-emerald-600">
                              R$ {Number(item.purchase_price).toFixed(2)}
                            </span>
                          )}
                          {item.category && (
                            <span className="text-[10px] bg-zinc-100 text-zinc-500 px-1.5 py-0.5 rounded">
                              {item.category}
                            </span>
                          )}
                        </>
                      )}
                    </div>
                  </div>
                </button>
              );
            })
          )}
        </div>

        {/* Export button */}
        <div className="flex justify-between items-center pt-1">
          <Button variant="outline" size="sm" onClick={onClose}>
            Cancelar
          </Button>
          <Button
            onClick={handleExport}
            disabled={selected.length === 0 || exporting}
            className="bg-emerald-600 hover:bg-emerald-700 text-white"
            data-testid="export-confirm-btn"
          >
            {exporting ? (
              <SpinnerGap size={16} className="mr-2 animate-spin" />
            ) : (
              <Download size={16} className="mr-2" />
            )}
            Exportar {selected.length > 0 ? `(${selected.length})` : ""}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
